import { IconArrowRight } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useSeatOffer } from '../lib/billing';
import { orgHref } from '../lib/org';
import { accountKind, SESSION_TOKEN, useMe } from '../lib/session';
import { createToast } from '../lib/toast';
import { SeatCheckout } from './SeatCheckout';
import { Toast } from './Toast';
import { buttonClass } from './ui/Button';

// "Put AI devs to work" — the onboarding seat step (arc 101 slice 07). The
// funnel ends with a live maintainer loop on the free tier and never mentions
// that AI-dev seats exist, so the only paid-activation path was for the user to
// find Billing on their own.
//
// Deliberately a client-side card on the terminal screen, NOT a new server-driven
// funnel state (same call as `OnboardingReachYou`): the funnel is account-level
// and gates activation, and an account is fully usable unpaid — parking it on a
// "pay now" state would make the free tier unreachable. `useSeatOffer` is the same
// gate the Billing panel uses, so the card is absent for an account that already
// has seats and for a caller without billing authority.
export function OnboardingBuySeats(props: { token?: string | null }): JSX.Element {
  // `=== undefined`, never `??` — the rule the parent route spends five lines on
  // (`routes/Onboarding.tsx`): an explicit `null` MEANS signed out and must
  // survive, and `??` swallows it, leaving `useSeatOffer` to query with the
  // production `__cookie_session__` marker for a caller who has no session.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const offer = useSeatOffer(token);
  // Solo is personal-accounts-only, so the cart needs the account's KIND.
  // `useSeatOffer` already reads `/v1/me` under this key — a cache read.
  const me = useMe(token);
  const { toast, notify, dismiss } = createToast();

  return (
    <Show when={offer()}>
      <div class="card card--footered onboarding__seats">
        <div class="card__header">
          <h2 class="card__title">{t('onboarding.seats_heading')}</h2>
          <p class="card__desc">{t('onboarding.seats_body')}</p>
        </div>

        <div class="card__content">
          <SeatCheckout token={token} accountKind={accountKind(me.data?.kind)} onNotice={notify} />
        </div>

        {/* Skip lane — the account works unpaid, so leaving the funnel without
            buying is a first-class outcome. Plain <a> (full nav) like its sibling
            done-card CTAs: no stale onboarding state after the hand-off. */}
        <div class="card__footer">
          <a
            class={buttonClass({ variant: 'outline', size: 'sm' })}
            href={orgHref('/account/billing')}
          >
            {t('onboarding.seats_later_cta')}
            <IconArrowRight size="0.875rem" />
          </a>
        </div>
      </div>
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'error'}
        onDismiss={dismiss}
      />
    </Show>
  );
}
